import { getFile, getFolder, type Folder, type Path } from "./state"

export type JsonSchema = {
  type?: string
  const?: unknown
  default?: unknown
  enum?: unknown[]
  required?: string[]
  properties?: Record<string, JsonSchema>
  items?: JsonSchema
  allOf?: JsonSchema[]
  anyOf?: JsonSchema[]
  [key: string]: unknown
}

export type CategoryNode = {
  description: string
  schema: JsonSchema | Path
  subcategories: Record<string, CategoryNode>
}

export type TaxonomyNode = {
  description: string
  schema: JsonSchema | Path
  categories: Record<string, CategoryNode>
}

export type TaxonomyItem = {
  path: Path
  value: Record<string, unknown>
}

export type ItemComment = {
  author: string
  content: string
  timestamp: number
}

const parseObject = (value: string | undefined): Record<string, unknown> | undefined => {
  if (value === undefined) return undefined
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : undefined
  } catch { return undefined }
}

export function readTaxonomy(state: Folder): TaxonomyNode {
  return (parseObject(getFile(state, ["taxonomy.json"])) as TaxonomyNode | undefined)
    ?? {description: "", schema: {type: "object"}, categories: {}}
}

export function taxonomyNode(root: TaxonomyNode, path: Path): TaxonomyNode | CategoryNode | undefined {
  if (!path.length) return root
  let node: CategoryNode | undefined = root.categories[path[0]!]
  for (const name of path.slice(1)) node = node?.subcategories[name]
  return node
}

export function categoryChildren(root: TaxonomyNode, path: Path): Record<string, CategoryNode> {
  const node = taxonomyNode(root, path)
  if (!node) return {}
  return "categories" in node ? node.categories : node.subcategories
}

export function categoryNames(root: TaxonomyNode, path: Path): string[] {
  return Object.keys(categoryChildren(root, path)).sort()
}

export function itemsIn(state: Folder, categoryPath: Path): TaxonomyItem[] {
  const folder = getFolder(state, ["data", ...categoryPath])
  if (!folder) return []
  return Object.keys(folder.files).sort().flatMap(name => {
    if (!name.endsWith(".json")) return []
    const path = ["data", ...categoryPath, name]
    const value = parseObject(folder.files[name])
    return value ? [{path, value}] : []
  })
}

function resolveSchema(state: Folder, schema: JsonSchema | Path | undefined): JsonSchema | undefined {
  if (!schema) return undefined
  if (!Array.isArray(schema)) return schema
  return parseObject(getFile(state, schema)) as JsonSchema | undefined
}

function schemaParts(schema: JsonSchema | undefined): JsonSchema[] {
  if (!schema) return []
  return [schema, ...(schema.allOf ?? []).flatMap(schemaParts)]
}

export function effectiveSchema(state: Folder, root: TaxonomyNode, categoryPath: Path): JsonSchema {
  const nodes: (TaxonomyNode | CategoryNode)[] = [root]
  let node: CategoryNode | undefined
  for (const [index, name] of categoryPath.entries()) {
    node = index === 0 ? root.categories[name] : node?.subcategories[name]
    if (!node) break
    nodes.push(node)
  }

  const required = new Set<string>()
  const properties: Record<string, JsonSchema> = {}
  for (const current of nodes) {
    for (const schema of schemaParts(resolveSchema(state, current.schema))) {
      for (const name of schema.required ?? []) required.add(name)
      Object.assign(properties, schema.properties ?? {})
    }
  }
  return {type: "object", required: [...required], properties}
}

function defaultValue(schema: JsonSchema | undefined): unknown {
  if (!schema) return ""
  if ("default" in schema) return structuredClone(schema.default)
  if ("const" in schema) return structuredClone(schema.const)
  if (schema.enum?.length) return structuredClone(schema.enum[0])
  if (schema.anyOf?.length) return defaultValue(schema.anyOf[0])
  switch (schema.type) {
    case "number": return 0
    case "boolean": return false
    case "array": return []
    case "object": return Object.fromEntries((schema.required ?? []).map(name => [name, defaultValue(schema.properties?.[name])]))
    case "null": return null
    default: return ""
  }
}

export function createItem(schema: JsonSchema, title: string): Record<string, unknown> {
  const item = Object.fromEntries((schema.required ?? []).map(name => [name, defaultValue(schema.properties?.[name])]))
  item.id = typeof item.id === "string" && item.id ? item.id : crypto.randomUUID()
  item.title = schema.properties?.title?.const ?? title
  return item
}

export function slug(value: string): string {
  return value.trim().toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

export const commentPath = (itemPath: Path): Path => [".etindi", "comments", ...itemPath]

export function readComments(state: Folder, itemPath: Path): ItemComment[] {
  const value = getFile(state, commentPath(itemPath))
  if (!value) return []
  try {
    const comments = JSON.parse(value)
    return Array.isArray(comments) ? comments.filter(comment =>
      comment && typeof comment.author === "string" && typeof comment.content === "string" && typeof comment.timestamp === "number"
    ) : []
  } catch { return [] }
}
